export { mergeProps } from '@zag-js/core';
import { createNormalizer } from '@zag-js/types';
import { subscribe, snapshot } from '@zag-js/store';
import { shallowRef, onUnmounted, watchEffect, watch, unref, onMounted, onBeforeUnmount } from 'vue';

// src/index.ts
function toCase(txt) {
  return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
}
var propMap = {
  htmlFor: "for",
  className: "class",
  onDoubleClick: "onDblclick",
  onChange: "onInput",
  onFocus: "onFocusin",
  onBlur: "onFocusout",
  defaultValue: "value",
  defaultChecked: "checked"
};
var preserveKeys = "viewBox,className,preserveAspectRatio,fillRule,clipPath,clipRule,strokeWidth,strokeLinecap,strokeLinejoin,strokeDasharray,strokeDashoffset,strokeMiterlimit".split(
  ","
);
function toVueProp(prop) {
  if (prop in propMap) return propMap[prop];
  if (prop.startsWith("on")) return `on${toCase(prop.substr(2))}`;
  if (preserveKeys.includes(prop)) return prop;
  return prop.toLowerCase();
}
var normalizeProps = createNormalizer((props) => {
  const normalized = {};
  for (const key in props) {
    const value = props[key];
    if (key === "children") {
      if (typeof value === "string") {
        normalized["innerHTML"] = value;
      } else if (process.env.NODE_ENV !== "production" && value != null) {
        console.warn("[Vue Normalize Prop] : avoid passing non-primitive value as `children`");
      }
    } else {
      normalized[toVueProp(key)] = props[key];
    }
  }
  return normalized;
});
function useSnapshot(service, options) {
  const { actions, context } = options ?? {};
  const state = shallowRef(service.state);
  const unsubscribe = subscribe(service.state, () => {
    state.value = snapshot(service.state);
  });
  onUnmounted(() => {
    unsubscribe?.();
  });
  watchEffect(() => {
    service.setOptions({ actions });
  });
  if (context) {
    watch(
      context,
      (ctx) => {
        service.setContext(unref(ctx));
      },
      { deep: true }
    );
  }
  return state;
}

// src/use-actor.ts
function useActor(service) {
  const state = useSnapshot(service);
  return [state, service.send];
}
function useService(machine, options) {
  const { state: hydratedState, context } = options ?? {};
  const service = typeof machine === "function" ? machine() : machine;
  if (context) service.setContext(unref(context));
  service._created();
  onMounted(() => {
    service.start(hydratedState);
    onBeforeUnmount(() => {
      service.stop();
    });
  });
  return service;
}

// src/use-machine.ts
function useMachine(machine, options) {
  const service = useService(machine, options);
  const state = useSnapshot(service, options);
  return [state, service.send, service];
}

export { normalizeProps, useActor, useMachine, useSnapshot };
